import React, { useState, useEffect } from "react";
import SEOHead from "../../common/SEOHead/SEOHead";
import { Link } from "react-router-dom";
import { get } from "../../../api/api";
import "./PickupSoccer.css";

function PickupSoccer() {
  const [selectedState, setSelectedState] = useState("MN");
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(true);

  const states = [
    { code: "MN", name: "Minnesota" },
    { code: "WI", name: "Wisconsin" },
    { code: "IA", name: "Iowa" },
    { code: "ND", name: "North Dakota" },
    { code: "SD", name: "South Dakota" },
    { code: "IL", name: "Illinois" },
    { code: "MI", name: "Michigan" },
    { code: "CA", name: "California" },
    { code: "TX", name: "Texas" },
    { code: "FL", name: "Florida" },
    { code: "NY", name: "New York" },
    { code: "PA", name: "Pennsylvania" },
  ];

  const fetchGames = async (state) => {
    setLoading(true);
    setError(null);
    setHasSearched(true);

    try {
      const response = await get(`/games/public/by-state?state=${state}`);
      setGames(response || []);
    } catch (err) {
      setError("Failed to load games. Please try again.");
      setGames([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch Minnesota games on page load
  useEffect(() => {
    fetchGames("MN");
  }, []);

  const handleStateChange = async (e) => {
    const state = e.target.value;
    setSelectedState(state);

    if (!state) {
      setGames([]);
      setHasSearched(false);
      setError(null);
      return;
    }

    fetchGames(state);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return "TBD";
    const date = new Date(timeString);
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? "PM" : "AM";
    const displayHour = hours % 12 || 12;
    const displayMinutes = minutes.toString().padStart(2, "0");
    return `${displayHour}:${displayMinutes} ${ampm}`;
  };

  const formatEndTime = (startTimeString) => {
    if (!startTimeString) return "TBD";
    const startDate = new Date(startTimeString);
    const endDate = new Date(startDate.getTime() + 60 * 60 * 1000); // Add 1 hour
    const hours = endDate.getHours();
    const minutes = endDate.getMinutes();
    const ampm = hours >= 12 ? "PM" : "AM";
    const displayHour = hours % 12 || 12;
    const displayMinutes = minutes.toString().padStart(2, "0");
    return `${displayHour}:${displayMinutes} ${ampm}`;
  };
  // Structured data for pickup soccer service
  const pickupSoccerStructuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        name: "Pickup Soccer Games",
        description:
          "GoalFlex organizes pickup soccer games every day in your city. Find competitive and recreational soccer matches near you.",
        provider: {
          "@type": "Organization",
          name: "GoalFlex",
          url: "https://goalflex.net",
        },
        areaServed: {
          "@type": "State",
          name: "Minnesota",
        },
        serviceType: "SportsApplication",
        offers: {
          "@type": "Offer",
          price: "0",
          priceCurrency: "USD",
          availability: "https://schema.org/InStock",
        },
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: "4.8",
          reviewCount: "250",
        },
      },
      {
        "@type": "FAQPage",
        mainEntity: [
          {
            "@type": "Question",
            name: "How does the GoalFlex pickup soccer work?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "GoalFlex connects you to pickup soccer games in your area. Simply download the app, find games near you, and join with one tap. We organize games daily across Minnesota.",
            },
          },
          {
            "@type": "Question",
            name: "What skill levels can join pickup soccer games?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "We welcome all skill levels from beginner to advanced. Games are open to all players looking to enjoy pickup soccer in a fun, inclusive environment.",
            },
          },
          {
            "@type": "Question",
            name: "How much does it cost to join pickup soccer games?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "The GoalFlex app is free to download and use. Individual game costs vary by location and field quality, typically ranging from $5-15 per person.",
            },
          },
        ],
      },
    ],
  };

  return (
    <div className="pickup-soccer-page">
      <SEOHead
        title="Pickup Soccer Games Near You | GoalFlex Minnesota"
        description="Find and join pickup soccer games in Minnesota. GoalFlex organizes daily soccer matches for all skill levels. Download the app and start playing today!"
        keywords="pickup soccer Minnesota, soccer games near me, recreational soccer, casual soccer games, soccer community Minnesota, join soccer games"
        url="https://goalflex.net/pickup-soccer"
        structuredData={pickupSoccerStructuredData}
      />

      {/* Hero Section */}
      <section className="hero-section">
        <div className="container">
          <div className="hero-content">
            <h1>Pickup Soccer Games</h1>
            <p className="hero-subtitle">
              GoalFlex organizes pickup soccer games every day in your city.
              Find competitive and recreational matches, connect with players,
              and never miss a game again.
            </p>
            <div className="hero-stats">
              <div className="stat">
                <strong>500+</strong>
                <span>Active Players</span>
              </div>
              <div className="stat">
                <strong>25+</strong>
                <span>Weekly Games</span>
              </div>
              <div className="stat">
                <strong>Minnesota</strong>
                <span>Service Area</span>
              </div>
            </div>
            <Link to="/download" className="cta-button">
              Join Games Today
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="container">
          <h2>Why Choose GoalFlex for Pickup Soccer?</h2>
          <div className="features-grid">
            <div className="feature">
              <h3>🏃‍♂️ All Skill Levels Welcome</h3>
              <p>
                From beginners to pros, we have games for everyone. Our smart
                matching ensures balanced, fun matches.
              </p>
            </div>
            <div className="feature">
              <h3>📍 Convenient Locations</h3>
              <p>
                Games at quality fields across Minnesota. Find matches close to
                home, work, or anywhere you are.
              </p>
            </div>
            <div className="feature">
              <h3>⚡ Instant Booking</h3>
              <p>
                Book your spot in seconds. One-tap reservations with instant
                confirmations and smart notifications.
              </p>
            </div>
            <div className="feature">
              <h3>👥 Verified Community</h3>
              <p>
                Play with verified players in a safe, friendly environment.
                Build lasting friendships through soccer.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="how-it-works">
        <div className="container">
          <h2>How Pickup Soccer Works</h2>
          <div className="steps">
            <div className="step">
              <div className="step-number">1</div>
              <h3>Download & Sign Up</h3>
              <p>
                Get the GoalFlex app and create your player profile in minutes.
              </p>
            </div>
            <div className="step">
              <div className="step-number">2</div>
              <h3>Find Games</h3>
              <p>Browse pickup soccer games by location and time.</p>
            </div>
            <div className="step">
              <div className="step-number">3</div>
              <h3>Join & Play</h3>
              <p>
                Reserve your spot and show up ready to play. Meet new players
                and have fun!
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Explore Available Games */}
      <section className="explore-games-section">
        <div className="container">
          <h2>Explore Available Games</h2>
          <p className="section-subtitle">
            Find pickup soccer games in your state. Select a state to see what's
            available.
          </p>

          <div className="state-selector">
            <label htmlFor="state-select">Select Your State:</label>
            <select
              id="state-select"
              value={selectedState}
              onChange={handleStateChange}
              className="state-dropdown"
            >
              <option value="">-- Choose a State --</option>
              {states.map((state) => (
                <option key={state.code} value={state.code}>
                  {state.name}
                </option>
              ))}
            </select>
          </div>

          {loading && (
            <div className="loading-message">
              <p>Loading games...</p>
            </div>
          )}

          {error && (
            <div className="error-message">
              <p>{error}</p>
            </div>
          )}

          {!loading && hasSearched && games.length === 0 && (
            <div className="no-games-message">
              <p>
                🎯 No games available in{" "}
                {states.find((s) => s.code === selectedState)?.name} yet.
              </p>
              <p>
                Check back soon or <Link to="/download">download the app</Link>{" "}
                to be notified when games are added!
              </p>
            </div>
          )}

          {!loading && games.length > 0 && (
            <div className="games-grid">
              {games.map((game) => (
                <div key={game.id} className="game-card">
                  <div className="game-header">
                    <h3>{game.title || "Pickup Soccer Game"}</h3>
                    <span
                      className={`game-status ${game.status?.toLowerCase()}`}
                    >
                      {game.status}
                    </span>
                  </div>
                  <div className="game-details">
                    <div className="game-info">
                      <span className="info-icon">📅</span>
                      <span>{formatDate(game.startAtUtc)}</span>
                    </div>
                    <div className="game-info">
                      <span className="info-icon">🕐</span>
                      <span>
                        {formatTime(game.startAtUtc)} -{" "}
                        {formatEndTime(game.startAtUtc)}
                      </span>
                    </div>
                    <div className="game-info">
                      <span className="info-icon">📍</span>
                      <span>{game.fieldName || game.location}</span>
                    </div>
                    <div className="game-info">
                      <span className="info-icon">👥</span>
                      <span>
                        {game.currentPlayers || 0} / {game.maxPlayers || 0}{" "}
                        players
                      </span>
                    </div>
                    {game.skillLevel && (
                      <div className="game-info">
                        <span className="info-icon">⚡</span>
                        <span>{game.skillLevel}</span>
                      </div>
                    )}
                  </div>
                  <Link to="/download" className="join-game-btn">
                    Join This Game
                  </Link>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <div className="container">
          <h2>Frequently Asked Questions</h2>
          <div className="faq-grid">
            <div className="faq-item">
              <h3>How does the GoalFlex pickup soccer work?</h3>
              <p>
                GoalFlex connects you to pickup soccer games in your area.
                Simply download the app, find games near you, and join with one
                tap. We organize games daily across Minnesota.
              </p>
            </div>
            <div className="faq-item">
              <h3>What skill levels can join pickup soccer games?</h3>
              <p>
                We welcome all skill levels from beginner to advanced. Games are
                open to all players looking to enjoy pickup soccer in a fun,
                inclusive environment.
              </p>
            </div>
            <div className="faq-item">
              <h3>How much does it cost to join pickup soccer games?</h3>
              <p>
                The GoalFlex app is free to download and use. Individual game
                costs vary by location and field quality, typically ranging from
                $5-15 per person.
              </p>
            </div>
            <div className="faq-item">
              <h3>What should I bring to a pickup soccer game?</h3>
              <p>
                Bring your cleats, shin guards, water bottle, and a positive
                attitude! We provide the balls and organize the teams.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <h2>Ready to Join the Soccer Community?</h2>
          <p>
            Download GoalFlex today and start playing pickup soccer games in
            Minnesota!
          </p>
          <div className="download-buttons">
            <a
              href="https://apps.apple.com/us/app/goalflex/id6745612755"
              target="_blank"
              rel="noopener noreferrer"
              className="download-btn apple"
            >
              Download for iOS
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=com.goalflex.app&pcampaignid=web_share"
              target="_blank"
              rel="noopener noreferrer"
              className="download-btn google"
            >
              Download for Android
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

export default PickupSoccer;
